import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { TierSpectrumRow } from '../../tiers/resolveTierContext';
import { fmtUsd } from './format';
import TierChip from './TierChip';
import { TIER_CATALOG } from '../../tiers/tierCatalog';
import TierPerformanceOverTimeChart from './TierPerformanceOverTimeChart';

interface Props {
  row: TierSpectrumRow;
  accountId?: string;
  children: React.ReactNode;
  emptyMessage?: string;
  showPerformanceChart?: boolean;
}

const TierPositionSection: React.FC<Props> = ({
  row,
  accountId,
  children,
  emptyMessage = 'No positions in this tier.',
  showPerformanceChart = true,
}) => {
  const catalog = TIER_CATALOG[row.riskTier];
  const budget = row.budget;
  const deployed = budget?.tierDeployedUsd ?? 0;
  const cap = budget?.tierBudgetUsd ?? 0;
  const utilPct = cap > 0 ? Math.min(100, (deployed / cap) * 100) : 0;

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <header className="border-b border-gray-100 bg-gray-50/80 px-4 py-3 sm:px-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <TierChip riskTier={row.riskTier} variant={row.enabled ? 'default' : 'inactive'} />
              {!row.enabled ? (
                <span className="text-xs text-gray-500">Not enabled on this account</span>
              ) : null}
            </div>
            <p className="mt-1 text-sm text-gray-600">{catalog.focus}</p>
          </div>
          <Link
            to={row.deepLink}
            className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            Open tier
            <ChevronRight className="ml-0.5 h-4 w-4" />
          </Link>
        </div>
        {row.enabled && budget ? (
          <div className="mt-3">
            <div className="flex justify-between text-xs text-gray-500">
              <span>
                Deployed {fmtUsd(deployed)} / {fmtUsd(cap)}
              </span>
              <span>{utilPct.toFixed(0)}% utilized</span>
            </div>
            <div className="mt-1 h-2 overflow-hidden rounded-full bg-gray-200">
              <div
                className={`h-full ${catalog.railClass}`}
                style={{ width: `${utilPct}%` }}
              />
            </div>
          </div>
        ) : null}
        <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
          <span>{row.positionCounts.active} active</span>
          <span>{row.positionCounts.pending} pending</span>
          <span>{row.positionCounts.recommendations} suggestions</span>
        </div>
        {showPerformanceChart && accountId ? (
          <div className="mt-4">
            <TierPerformanceOverTimeChart
              accountId={accountId}
              riskTier={row.riskTier}
              compact
            />
          </div>
        ) : null}
      </header>
      <div className="p-4 sm:p-5">
        {React.Children.count(children) === 0 ? (
          <p className="text-sm text-gray-500">{emptyMessage}</p>
        ) : (
          children
        )}
      </div>
    </section>
  );
};

export default TierPositionSection;
